import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import ShareCard from "@/components/ShareCard";
import { Download, Share2, X } from "lucide-react";

/**
 * Floating modal that renders the ShareCard and provides download + native share.
 * Open it by toggling `open` state from the parent.
 */
export default function ShareModal({ open, onClose, variant, userName, journeyTitle, journeyCover, hours, moduleTitle }) {
    const cardRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [shared, setShared] = useState("");

    if (!open) return null;

    const generatePng = async () => {
        if (!cardRef.current) return null;
        return await toPng(cardRef.current, {
            cacheBust: true,
            pixelRatio: 2,
            backgroundColor: "#050507",
        });
    };

    const download = async () => {
        try {
            setBusy(true);
            const dataUrl = await generatePng();
            if (!dataUrl) return;
            const link = document.createElement("a");
            link.download = `imq-${variant}-${(userName || "card").toLowerCase().replace(/\s+/g, "-")}.png`;
            link.href = dataUrl;
            link.click();
            setShared("Imagem baixada!");
        } catch (e) {
            setShared("Não foi possível gerar a imagem.");
        } finally {
            setBusy(false);
        }
    };

    const shareNative = async () => {
        try {
            setBusy(true);
            const dataUrl = await generatePng();
            if (!dataUrl) return;
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], `imq-${variant}.png`, { type: "image/png" });
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: "Instituto Medicina Quântica",
                    text: variant === "certificate"
                        ? `Concluí a jornada "${journeyTitle}" no Instituto de Medicina Quântica.`
                        : `Avancei mais um módulo no Instituto de Medicina Quântica.`,
                });
                setShared("Compartilhado!");
            } else {
                await download();
            }
        } catch (e) {
            if (e?.name !== "AbortError") setShared("Compartilhamento cancelado.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="share-modal"
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
            onClick={onClose}
        >
            <div
                className="relative max-w-5xl w-full bg-[#0A0A0A] rounded-3xl border border-white/10 overflow-hidden flex flex-col md:flex-row"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="share-modal-close"
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                    aria-label="Fechar"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Preview (scaled) */}
                <div className="md:flex-1 bg-[#050507] flex items-center justify-center p-6 overflow-hidden">
                    <div style={{ transform: "scale(0.32)", transformOrigin: "center center", width: 1080, height: 1350 }}>
                        <ShareCard
                            ref={cardRef}
                            variant={variant}
                            userName={userName}
                            journeyTitle={journeyTitle}
                            journeyCover={journeyCover}
                            hours={hours}
                            moduleTitle={moduleTitle}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="md:w-[360px] p-8 md:p-10 text-white border-t md:border-t-0 md:border-l border-white/10">
                    <div className="overline text-white/60">Card compartilhável</div>
                    <h3 className="font-display text-2xl mt-2 tracking-tight">
                        Eternize {variant === "certificate" ? "sua jornada" : "esse passo"}
                    </h3>
                    <p className="font-body text-sm text-white/60 mt-3">
                        Baixe a imagem cinematográfica em alta resolução (1080×1350, ideal para feed e stories) ou compartilhe direto no seu app favorito.
                    </p>

                    <div className="mt-7 flex flex-col gap-3">
                        <button
                            data-testid="share-download"
                            onClick={download}
                            disabled={busy}
                            className="btn-holo w-full justify-center"
                        >
                            <Download className="w-4 h-4" />
                            {busy ? "Gerando..." : "Baixar PNG"}
                        </button>
                        <button
                            data-testid="share-native"
                            onClick={shareNative}
                            disabled={busy}
                            className="btn-ghost !bg-white/10 !text-white !border-white/15 w-full justify-center"
                        >
                            <Share2 className="w-4 h-4" />
                            Compartilhar
                        </button>
                    </div>

                    {shared && <div className="mt-4 text-xs text-white/60 font-body">{shared}</div>}

                    <div className="mt-10 text-xs font-body text-white/40 leading-relaxed">
                        Dica: poste no seu story marcando @institutomedicinaquantica para ser repostado no nosso canal.
                    </div>
                </div>
            </div>
        </div>
    );
}
